import React, { useState, useEffect } from 'react'
import { Button } from 'react-bootstrap'
import { useParams } from 'react-router-dom'
import axios from 'axios'
import { CommonLoading } from '../../../../Utils/commonLoading'
import toaster from '../../../../Utils/toaster'

const API_URL = process.env.REACT_APP_API_URL
export function QuickDialsView() {
  const [isLoading, setIsLoading] = useState(true)
  const [languages, setLanguages] = useState<{ key: string; value: string }[]>([])
  const [isLoadingLanguages, setIsLoadingLanguages] = useState(false)
  const { id } = useParams<{ id: string }>()
  const [hasQuickDials, setHasQuickDials] = useState(false)
  const [isScheduled, setIsScheduled] = useState(false)
  const [langs, setLangs] = useState<(string | null)[]>([]);

  // Fetch languages from customer API
  const fetchCustomerLanguages = async (customerCode: string | undefined) => {
    if (!customerCode) return;
    setIsLoadingLanguages(true);
    try {
      const response = await axios.get(`${API_URL}/customer/${customerCode}/languages`)
      // Transform the response to match the expected format
      // API response structure: { status, text, code, result: [{ code, name }] }
      const responseData = response?.data?.result || [];
      const transformed = Array.isArray(responseData) ? responseData.map((item: any) => ({
        key: item.code,
        value: item.name
      })) : [];
      // Add Operator option with key "OPI"
      const languagesWithOperator = [
        ...transformed,
        { key: 'OPI', value: 'Operator' }
      ];
      setLanguages(languagesWithOperator);
      setIsLoadingLanguages(false);
    } catch (error) {
      console.error(error)
      setLanguages([]);
      setIsLoadingLanguages(false);
    } finally {
      setIsLoadingLanguages(false);
    }
  }

  useEffect(() => {
    if (id) {
      fetchCustomerLanguages(id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])

  // Validate existing languages against available languages
  // If a previously saved language is no longer available, set it to null
  useEffect(() => {
    if (languages.length > 0 && langs.length > 0) {
      // Create a set of available language codes (case-insensitive, but preserve OPI as uppercase)
      const availableLanguageCodes = new Set(
        languages.map(lang => lang.key.toLowerCase())
      );
      
      // Validate and clean up langs array
      setLangs(prev => {
        const validated = prev.map(lang => {
          if (lang === null) return null;
          // Check if the language code exists in available languages (case-insensitive)
          const langCode = lang.toLowerCase();
          // If it's OPI, keep it uppercase; otherwise keep as is
          if (availableLanguageCodes.has(langCode)) {
            return langCode === 'opi' ? 'OPI' : lang;
          }
          return null;
        });
        return validated;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [languages])

  const getExistingQuickDials = async (customerId: string | undefined) => {
    setIsLoading(true);
    if (!id) return;
    try {
      const response = await axios.get(`${API_URL}/customerhasquickdial/GetByCustomerId/${id}`)
      setHasQuickDials(response?.data?.hasQuickDial);
      setIsScheduled(!!response?.data?.isScheduled);
      setLangs(response?.data?.languages || []);
      setIsLoading(false)
    } catch (error) {
      console.error(error)
      toaster("error",'Error fetching Quick Dials');
      setIsLoading(false) 
    }
  }

  const postQuickDials = async (customerId: string | undefined, hasQuickDials: boolean, isScheduled: boolean, langs: (string | null)[]) => {
    setIsLoading(true);

    if (!customerId) return; // Also fixed: you used 'id' instead of 'customerId'
    try {
      await axios.put(`${API_URL}/customerhasquickdial/UpdateByCustomerCode`, {
        customerId: customerId, // Also fixed: you used 'id' instead of 'customerId'
        hasQuickDial: hasQuickDials,
        isScheduled,
        languages: langs
      });
            toaster("success",'Quick Dials updated successfully');

          setIsLoading(false);

    } catch (error) {
      console.error(error);
            toaster("error",'Unable to Update Quick Dials');

          setIsLoading(false);

    }
  }



  useEffect(() => {
    getExistingQuickDials(id)
  }, [])

  const handlerFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    postQuickDials(id, hasQuickDials, isScheduled, langs);
  }



  return (
    <>
      <div className='modal fade' tabIndex={-1} id='kt_quick_dials'>
        <div className='modal-dialog modal-lg'>
          <div className='modal-content'>
            <div className='modal-header py-2'>
              <h4 className='modal-title'>Edit Quick Dials</h4>
            </div>
          </div>
        </div>
      </div>

      <div className='card-body p-0'>
        <div className='card-title d-flex align-items-center me-4 mb-3'>
          <div className='d-flex flex-column'>
            <div className='d-flex align-items-center'>
              <h5 className='text-black fs-4 fw-semibold mb-0'>Quick Dials</h5>
            </div>
            <div className='d-flex flex-wrap fs-6'>
              <p className='text-gray-500 mb-0 mt-4 small w-100' /* removed maxWidth style, added small and w-100 */>
                Adding a language/service type pairing below will create an IVR menu AND update
                dialer mobile apps for quick, one touch, selection. Note: All Quick Dials settings
                that include Video will not be included in IVR menu. Request Service type can only
                be used for IVR system.
              </p>
            </div>
          </div>
        </div>
        {(isLoading || isLoadingLanguages) ? <CommonLoading/> : <React.Fragment>
          <div>


            <div className='form-check form-switch d-flex align-items-center mt-5'>
              <input
                className='form-check-input w-45px h-30px cursor-pointer'
                type='checkbox'
                id='hasQuickDials'
                checked={hasQuickDials}
                onChange={() => {
                  const next = !hasQuickDials
                  setHasQuickDials(next)
                  if (!next) setIsScheduled(false)
                }}
              />
              <label className='form-check-label ml-4 d-inline-block ms-3 text-muted fs-5 cursor-pointer' htmlFor='hasQuickDials'>Enable Quick Dial</label>
            </div>
            <div className='form-check form-switch d-flex align-items-center mt-4'>
              <input
                className='form-check-input w-45px h-30px cursor-pointer'
                type='checkbox'
                id='isScheduled'
                checked={isScheduled}
                disabled={!hasQuickDials}
                onChange={() => setIsScheduled(!isScheduled)}
              />
              <label
                className={`form-check-label ml-4 d-inline-block ms-3 fs-5 ${hasQuickDials ? 'text-muted cursor-pointer' : 'text-gray-400'}`}
                htmlFor='isScheduled'
              >
                Scheduled Function
              </label>
            </div>
          </div>
          <div className="mt-5">
            <form
              onSubmit={e => { handlerFormSubmit(e)}}
            >
              <div className="table-responsive">
                <table className="table align-middle">
                  <thead>
                    <tr>
                      <th className='fs-3 fw-bold'>Dialer Code</th>
                      <th className='fs-3 fw-bold'>Languages</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Array.from({ length: 9 }).map((_, idx) => (
                      <tr key={idx}>
                        <td
                          className='fs-3 text-center'>
                          <span className="d-inline-block w-120px min-w-90px max-w-120px fw-bold fs-3">{idx + 1}</span>
                        </td>
                        <td>
                          <DropDownMenu currentIteration={idx} languageList={languages} inputName={`langauge${idx}`} langs={langs} setLangs={setLangs} />

                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="d-flex justify-content-end mt-3">
                <Button variant='primary' type='submit'>
                  Update
                </Button>
              </div>
            </form>
          </div>
        </React.Fragment>}
      </div>
    </>
  )
}

type DropDownMenuProps = {
  languageList: { key: string; value: string }[]
  langName?: string
  inputName?: string
  langs: (string | null)[]
  currentIteration?: number
  setLangs: React.Dispatch<React.SetStateAction<(string | null)[]>>
}

export const DropDownMenu: React.FC<DropDownMenuProps> = ({
  languageList,
  langName,
  langs,
  setLangs,
  inputName,
  currentIteration
}) => {
  // Ensure langs array always has 9 elements (null if not set)
  useEffect(() => {
    if (langs.length < 9) {
      setLangs(prev => {
        const filled = [...prev]
        while (filled.length < 9) filled.push(null)
        return filled
      })
    }
    // eslint-disable-next-line
  }, [])

  const handlerOnChange = (
    e: React.ChangeEvent<HTMLSelectElement>,
    index: number | undefined
  ) => {
    setLangs(prev => {
      const newLangs = [...prev]
      if (index !== undefined) {
        const value = e.target.value
        // Keep "OPI" in uppercase, convert others to lowercase
        newLangs[index] = value === '' ? null : (value.toUpperCase() === 'OPI' ? 'OPI' : value.toLowerCase())
      }
      return newLangs
    })
    console.log(langs)
  }

  // Filter out languages that are already selected in other dropdowns
  // But always include the currently selected language for this dropdown
  const getAvailableLanguages = () => {
    if (typeof currentIteration !== 'number') {
      return languageList
    }
    
    // Get the currently selected language for this dropdown (case-insensitive comparison)
    const currentLanguage = langs[currentIteration]?.toLowerCase() || null
    
    // Get all languages that are selected in other indices (excluding current)
    const selectedLanguages = langs
      .map((lang, idx) => idx !== currentIteration && lang ? lang.toLowerCase() : null)
      .filter((lang): lang is string => lang !== null)
    
    // Filter out already selected languages, but always include the current selection
    return languageList.filter(lang => {
      const langKey = lang.key.toLowerCase()
      return langKey === currentLanguage || !selectedLanguages.includes(langKey)
    })
  }

  return (
    <select
      className='form-select inline-block form-select-solid form-select-md select2-hidden-accessible quick-dial-select w-100'
      name={inputName}
      id={inputName}
      value={
        typeof currentIteration === 'number' && langs[currentIteration] != null
          ? langs[currentIteration]!.toUpperCase() === 'OPI' ? 'OPI' : langs[currentIteration]!
          : ''
      }
      onChange={e => handlerOnChange(e, currentIteration)}
    >
      <option value="">
        {langName || 'Select Language'}
      </option>
      {getAvailableLanguages().map(lang => (
        <option key={lang.key} value={lang.key.toUpperCase() === 'OPI' ? 'OPI' : lang.key.toLowerCase()}>
          {lang.value}
        </option>
      ))}
    </select>
  )
}